import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Send, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { key: 'amazon', label: 'أمازون', color: 'bg-orange-500' },
  { key: 'noon', label: 'نون', color: 'bg-yellow-500' },
  { key: 'namshi', label: 'نمشي', color: 'bg-pink-500' },
  { key: 'aliexpress', label: 'علي إكسبريس', color: 'bg-red-500' }
];

export default function TelegramSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    navigate('/admin');
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/telegram/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('انتهت جلسة الدخول، يرجى تسجيل الدخول مجدداً');
        handleLogout();
      } else {
        console.error('خطأ في تحميل الإعدادات:', error);
        toast.error('فشل تحميل الإعدادات');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/telegram/settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('انتهت جلسة الدخول، يرجى تسجيل الدخول مجدداً');
        handleLogout();
      } else {
        toast.error('فشل حفظ الإعدادات');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (category) => {
    // Get current data from state (not from database)
    const bot_token = settings[`${category}_bot_token`];
    const channel = settings[`${category}_channel`];
    
    if (!bot_token || !channel) {
      toast.error('⚠️ يرجى إدخال Bot Token ومعرف القناة أولاً');
      return;
    }
    
    setTesting({ ...testing, [category]: true });
    try {
      // Test directly with current data
      const testUrl = `https://api.telegram.org/bot${bot_token}/getMe`;
      const response = await fetch(testUrl);
      const data = await response.json();
      
      if (response.ok && data.ok) {
        toast.success(`✅ الاتصال ناجح - البوت: @${data.result.username}`);
      } else {
        toast.error('❌ فشل الاتصال - تحقق من Bot Token');
      }
    } catch (error) {
      toast.error(`❌ خطأ: ${error.message}`);
    } finally {
      setTesting({ ...testing, [category]: false });
    }
  };

  const updateField = (category, field, value) => {
    setSettings({
      ...settings,
      [`${category}_${field}`]: value
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">جارٍ التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4">
          <Link to="/admin/dashboard" className="inline-flex items-center text-primary hover:underline">
            <ArrowLeft className="ml-2 h-4 w-4" />
            العودة للوحة التحكم
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">إعدادات تيليجرام</h1>
          <p className="text-muted-foreground">
            قم بإعداد قنوات تيليجرام للنشر التلقائي حسب التصنيف
          </p>
        </div>

        <div className="space-y-6">
          {categories.map((category) => (
            <Card key={category.key}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${category.color}`}></div>
                    <CardTitle>قناة {category.label}</CardTitle>
                  </div>
                  <Switch
                    checked={settings[`${category.key}_enabled`] || false}
                    onCheckedChange={(checked) => updateField(category.key, 'enabled', checked)}
                  />
                </div>
                <CardDescription>
                  {settings[`${category.key}_enabled`] 
                    ? '✅ القناة مفعّلة - سيتم النشر التلقائي' 
                    : '⚠️ القناة غير مفعّلة'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Bot Token</Label>
                  <Input
                    type="password"
                    value={settings[`${category.key}_bot_token`] || ''}
                    onChange={(e) => updateField(category.key, 'bot_token', e.target.value)}
                    placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    className="admin-input font-mono text-sm"
                    autoComplete="new-password"
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    spellCheck="false"
                  />
                  <p className="text-xs text-muted-foreground">💡 يمكنك لصق Token - لكن لا يمكن نسخه بعد ذلك</p>
                </div>

                <div className="space-y-2">
                  <Label>معرف القناة</Label>
                  <Input
                    value={settings[`${category.key}_channel`] || ''}
                    onChange={(e) => updateField(category.key, 'channel', e.target.value)}
                    placeholder="@oroodh_amazon أو -1001234567890"
                    className="admin-input"
                    dir="ltr"
                  />
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTest(category.key)}
                  disabled={testing[category.key] || !settings[`${category.key}_bot_token`] || !settings[`${category.key}_channel`]}
                  className="w-full"
                >
                  {testing[category.key] ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current ml-2"></div>
                      جارٍ الاختبار...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="ml-2 h-4 w-4" />
                      اختبار الاتصال
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 flex gap-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex-1"
          >
            {saving ? 'جارٍ الحفظ...' : 'حفظ الإعدادات'}
          </Button>
        </div>

        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-900">📘 كيفية الحصول على Bot Token</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-blue-900 space-y-2">
            <p>1. افتح تيليجرام وابحث عن <code className="bg-blue-100 px-2 py-1 rounded">@BotFather</code></p>
            <p>2. أرسل الأمر <code className="bg-blue-100 px-2 py-1 rounded">/newbot</code></p>
            <p>3. اتبع التعليمات وسيعطيك Token</p>
            <p>4. أضف البوت كمسؤول في قناتك</p>
            <p>5. انسخ معرف القناة (مثل: @oroodh_amazon)</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
